import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { api } from "@/lib/api";

const field = "w-full px-4 py-2.5 rounded-xl border border-[#DCE6EF] bg-white focus:outline-none focus:ring-2 focus:ring-[#2472C8]/40";
const fields = [
  { key: "company_name", label: "Nome studio" },
  { key: "whatsapp_number", label: "Numero WhatsApp (solo cifre, con prefisso internazionale)" },
  { key: "phone", label: "Telefono" },
  { key: "email", label: "Email" },
  { key: "address", label: "Indirizzo" },
];

export default function AdminSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/settings")).data });
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data) setF(data); }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { data: f });
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error("Errore nel salvataggio"); } finally { setSaving(false); }
  };

  const uploadTariffario = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/admin/upload", fd);
      setF((prev) => ({ ...prev, tariffario_url: res.url }));
      toast.success("Tariffario caricato. Premi 'Salva impostazioni' per confermare.");
    } catch { toast.error("Errore nel caricamento del file"); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl font-bold text-[#1B2A41]">Impostazioni</h1>
      <p className="text-[#566476] mt-1">Dati di contatto e numero WhatsApp usati in tutto il sito.</p>
      <div className="bg-white rounded-2xl border border-[#DCE6EF] p-6 mt-6 space-y-4">
        {fields.map((fl) => (
          <div key={fl.key}>
            <label className="block text-sm font-medium text-[#1B2A41] mb-2">{fl.label}</label>
            <input value={f[fl.key] || ""} onChange={(e) => setF({ ...f, [fl.key]: e.target.value })} className={field} data-testid={`setting-${fl.key}`} />
          </div>
        ))}
        <div>
          <label className="block text-sm font-medium text-[#1B2A41] mb-2">Tariffario condominio (PDF)</label>
          {f.tariffario_url ? (
            <a href={`${process.env.REACT_APP_BACKEND_URL}${f.tariffario_url}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#2472C8] font-semibold" data-testid="tariffario-current">Visualizza il tariffario caricato →</a>
          ) : (
            <p className="text-sm text-[#8A97A6]">Nessun file caricato.</p>
          )}
          <input type="file" accept="application/pdf" onChange={uploadTariffario} className="mt-2 block text-sm text-[#566476]" data-testid="tariffario-upload" />
        </div>
        <button onClick={save} disabled={saving} className="bg-[#2472C8] hover:bg-[#1E5FA6] text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2 disabled:opacity-60 transition-colors" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva impostazioni
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-[#DCE6EF] p-6 mt-6">
        <h2 className="font-heading font-semibold text-[#1B2A41]">Strumenti SEO</h2>
        <p className="text-sm text-[#566476] mt-1">Feed e sitemap generati automaticamente dai contenuti pubblicati.</p>
        <div className="flex flex-wrap gap-3 mt-4">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#2472C8] border border-[#DCE6EF] rounded-full px-4 py-2" data-testid="link-sitemap">sitemap.xml</a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/feed/immobiliare.xml`} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#2472C8] border border-[#DCE6EF] rounded-full px-4 py-2" data-testid="link-feed">Feed Immobiliare.it</a>
        </div>
      </div>
      <TwoFASection />
      <ChangePwSection />
      <UsersSection />
    </div>
  );
}

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [nf, setNf] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    setBusy(true);
    try { await api.post("/admin/users", nf); toast.success("Amministratore creato"); setNf({ name: "", email: "", password: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questo amministratore?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Amministratore eliminato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#DCE6EF] p-6 mt-6" data-testid="users-section">
      <h2 className="font-heading font-semibold text-[#1B2A41]">Gestione amministratori</h2>
      <p className="text-sm text-[#566476] mt-1">Crea o rimuovi account admin. Le password vengono cifrate e non sono mai visibili.</p>
      <div className="mt-4 divide-y divide-[#EEF2F6]">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between py-2.5" data-testid={`user-${u.email}`}>
            <div>
              <span className="font-medium text-[#1B2A41] text-sm">{u.name}</span>
              <span className="text-[#566476] text-sm"> · {u.email}</span>
              {u.twofa && <span className="ml-2 text-[11px] text-[#128C43] font-semibold">2FA</span>}
            </div>
            <button onClick={() => del(u.id)} className="text-xs font-semibold text-[#B42318] border border-[#F1C7C2] rounded-full px-3 py-1" data-testid={`user-del-${u.email}`}>Elimina</button>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-2 mt-4">
        <input value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} placeholder="Nome" className={field} data-testid="newuser-name" />
        <input type="email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} placeholder="Email" className={field} data-testid="newuser-email" />
        <input type="password" value={nf.password} onChange={(e) => setNf({ ...nf, password: e.target.value })} placeholder="Password (min 8)" className={field} data-testid="newuser-password" />
      </div>
      <button onClick={add} disabled={busy} className="mt-3 bg-[#1B2A41] text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-60" data-testid="newuser-add-btn">Crea amministratore</button>
    </div>
  );
}

function ChangePwSection() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password aggiornata con successo"); setCur(""); setNw(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#DCE6EF] p-6 mt-6" data-testid="changepw-section">
      <h2 className="font-heading font-semibold text-[#1B2A41]">Cambia la mia password</h2>
      <p className="text-sm text-[#566476] mt-1">Aggiorna la password del tuo account admin.</p>
      <div className="grid sm:grid-cols-2 gap-2 mt-4 max-w-lg">
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Password attuale" className={field} data-testid="changepw-current" />
        <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="Nuova password (min 8)" className={field} data-testid="changepw-new" />
      </div>
      <button onClick={submit} disabled={busy || !cur || !nw} className="mt-3 bg-[#2472C8] text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-60" data-testid="changepw-btn">Aggiorna password</button>
    </div>
  );
}

function TwoFASection() {
  const [enabled, setEnabled] = useState(null);
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/auth/2fa/status").then((r) => setEnabled(r.data.enabled)).catch(() => setEnabled(false)); }, []);
  const setup = async () => { setBusy(true); try { const { data } = await api.post("/auth/2fa/setup"); setQr(data.qr); } catch { toast.error("Errore"); } finally { setBusy(false); } };
  const enable = async () => { setBusy(true); try { await api.post("/auth/2fa/enable", { code }); toast.success("2FA attivato"); setEnabled(true); setQr(""); setCode(""); } catch (e) { toast.error(e.response?.data?.detail || "Codice non valido"); } finally { setBusy(false); } };
  const disable = async () => { setBusy(true); try { await api.post("/auth/2fa/disable", { code }); toast.success("2FA disattivato"); setEnabled(false); setCode(""); } catch (e) { toast.error(e.response?.data?.detail || "Codice non valido"); } finally { setBusy(false); } };
  return (
    <div className="bg-white rounded-2xl border border-[#DCE6EF] p-6 mt-6" data-testid="twofa-section">
      <h2 className="font-heading font-semibold text-[#1B2A41]">Sicurezza · Autenticazione a due fattori (2FA)</h2>
      <p className="text-sm text-[#566476] mt-1">Aggiungi un codice temporaneo dall'app authenticator (Google Authenticator, Authy) all'accesso admin.</p>
      {enabled === null ? <p className="text-sm mt-3 text-[#566476]">Caricamento…</p> : enabled ? (
        <div className="mt-4">
          <p className="text-sm text-[#128C43] font-semibold">✓ 2FA attivo su questo account</p>
          <div className="flex gap-2 mt-3 max-w-sm">
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Codice a 6 cifre" className={field} data-testid="twofa-code" />
            <button onClick={disable} disabled={busy} className="whitespace-nowrap text-sm font-semibold text-[#B42318] border border-[#F1C7C2] rounded-full px-4" data-testid="twofa-disable-btn">Disattiva</button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          {!qr ? (
            <button onClick={setup} disabled={busy} className="bg-[#1B2A41] text-white font-semibold px-5 py-2.5 rounded-full text-sm" data-testid="twofa-setup-btn">Configura 2FA</button>
          ) : (
            <div>
              <p className="text-sm text-[#566476]">1) Scansiona il QR con l'app authenticator. 2) Inserisci il codice generato per confermare.</p>
              <img src={qr} alt="QR 2FA" className="w-44 h-44 mt-3 border border-[#DCE6EF] rounded-xl" data-testid="twofa-qr" />
              <div className="flex gap-2 mt-3 max-w-sm">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Codice a 6 cifre" className={field} data-testid="twofa-code" />
                <button onClick={enable} disabled={busy} className="bg-[#2472C8] text-white font-semibold px-5 rounded-full text-sm" data-testid="twofa-enable-btn">Attiva</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
